/**
 * Reading AI position proposals off the assessment stream.
 *
 * The parser is deliberately forgiving about the envelope (the backend may
 * name the list `proposals`, `stances` or send it bare) but strict about the
 * contents: anything without a real subject and a real position is dropped
 * rather than shown to an analyst as if it were a finding.
 */
import type { StanceConfidence, SubjectRef } from '../../../types/issues';
import { clampPosition, subjectKey } from './format';

export interface StanceProposal {
  /** `stakeholder:<id>` — also the dedupe key */
  key: string;
  subject: SubjectRef;
  position: number;
  confidence: StanceConfidence;
  rationale: string | null;
  evidenceUrl: string | null;
  issueInfluence?: number;
}

/** A band from whatever the model produced: a word, or a 0–1 number */
export function toConfidence(value: unknown): StanceConfidence {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const scaled = value > 1 ? value / 100 : value;
    return scaled >= 0.7 ? 'high' : scaled >= 0.4 ? 'medium' : 'low';
  }
  const word = String(value ?? '').toLowerCase();
  if (word === 'high' || word === 'medium' || word === 'low') return word;
  return 'medium';
}

const asText = (value: unknown): string | null => {
  const text = typeof value === 'string' ? value.trim() : '';
  return text || null;
};

export function parseProposals(data: any): StanceProposal[] {
  const raw = Array.isArray(data)
    ? data
    : data?.proposals ?? data?.stances ?? data?.positions ?? data?.suggestions ?? [];
  if (!Array.isArray(raw)) return [];

  const seen = new Set<string>();
  const proposals: StanceProposal[] = [];
  raw.forEach((item: any) => {
    const type = item?.subject?.type ?? item?.subjectType;
    const id = item?.subject?.id ?? item?.subjectId;
    if ((type !== 'stakeholder' && type !== 'organization') || typeof id !== 'string' || !id) return;

    const position = Number(item?.position ?? item?.proposedPosition);
    if (!Number.isFinite(position)) return;

    const subject: SubjectRef = {
      type,
      id,
      label: asText(item?.subject?.label ?? item?.subjectLabel ?? item?.label) || 'Unnamed',
    };
    const key = subjectKey(subject);
    if (seen.has(key)) return;
    seen.add(key);

    const influence = Number(item?.issueInfluence ?? item?.influence);
    proposals.push({
      key,
      subject,
      position: clampPosition(position),
      confidence: toConfidence(item?.confidence),
      rationale: asText(item?.rationale ?? item?.reason ?? item?.why),
      evidenceUrl: asText(item?.evidenceUrl ?? item?.url ?? item?.source),
      ...(Number.isFinite(influence) ? { issueInfluence: Math.max(0, Math.min(100, Math.round(influence))) } : {}),
    });
  });
  return proposals;
}
